import type { WorkOrderTemplate } from '#/graphql/types';
import { categories, executionIntervals, impacts, priorities, types } from './constants';
import { gql } from '@apollo/client';
import { buildFilters } from '#/common/utils/buildFilters';
import {
  getAssetType,
  getAssetTypeList,
  getManufacturer,
  getManufacturerList,
  getModel,
  getModelList,
} from '#/graphql/queries';
import { inUseStatuses } from '#/modules/asset/manufacturer/utils/constants';
import FieldDefs from '#/types/FieldDefs';
import { getBaseColumns } from '#/common/utils/baseColumns';

const fields = {
  ...getBaseColumns(),
  id: {
    dataField: 'id',
    label: 'ID',
    editorOptions: { readOnly: true },
  },
  displayNumber: {
    dataField: 'displayNumber',
    label: 'Number',
    editorOptions: {
      readOnly: true,
      placeholder: 'Auto generated',
    },
  },
  title: {
    dataField: 'title',
    label: 'Title',
    isRequired: true,
  },

  assetManufacturerId: {
    dataField: 'assetManufacturerId',
    label: 'Asset Manufacturer',
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Search manufacturers...',
      button: {
        tooltip: 'View manufacturer details',
        url: '/assets/manufacturers/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      subtitleExpr: 'contactPerson',
      dataStoreOptions: {
        getItemList: gql(getManufacturerList),
        getItem: gql(getManufacturer),
        filter: ['inUseStatus', '=', inUseStatuses.inUse.key],
      },
    },
  },
  assetTypeId: {
    dataField: 'assetTypeId',
    label: 'Asset Type',
    editorType: 'RelatedItems',
    isRequired: true,
    editorOptions: {
      placeholder: 'Search asset types...',
      button: {
        tooltip: 'View asset type details',
        url: '/assets/types/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      subtitleExpr: 'category',
      dataStoreOptions: {
        getItemList: gql(getAssetTypeList),
        getItem: gql(getAssetType),
        filter: ['inUseStatus', '=', inUseStatuses.inUse.key],
      },
    },
  },
  assetModelId: {
    dataField: 'assetModelId',
    label: 'Asset Model',
    editorType: 'RelatedItems',
    editorOptions: {
      placeholder: 'Search models...',
      button: {
        tooltip: 'View asset model details',
        url: '/assets/models/:value/details',
        desktopOnly: false,
      },
      displayExpr: 'name',
      valueExpr: 'id',
      subtitleExpr: 'manufacturer.name',
      dataStoreOptions: {
        getItemList: gql(getModelList),
        getItem: gql(getModel),
        filter: ['inUseStatus', '=', inUseStatuses.inUse.key],
      },
      dynamic: {
        watch: ['assetTypeId', 'assetManufacturerId'],
        build: ({ record }) => {
          const typeId = record?.assetTypeId as string | undefined;
          const manId = record?.assetManufacturerId as string | undefined;

          if (!typeId && !manId) return null;

          const filterParts: string[][] = [];
          if (typeId) filterParts.push(['assetTypeId', '=', typeId]);
          if (manId) filterParts.push(['manufacturerId', '=', manId]);

          return buildFilters(filterParts);
        },

        // Example: react to parent changes (telemetry, side-effects, etc.)

        // onWatchChange: ({ changed /*, form, record, value */ }) => {
        //   console.log('Parents changed:', changed);
        // },
      },
    },
  },

  description: {
    dataField: 'description',
    label: 'Description',
    editorType: 'TextArea',
    isRequired: true,
    editorOptions: {
      placeholder: 'Enter description',
      autoResizeEnabled: true,
      height: 100,
    },
  },
  executionInterval: {
    dataField: 'executionInterval',
    label: 'Execution Interval',
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Choose an option',
      items: executionIntervals,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  category: {
    dataField: 'category',
    label: 'Category',
    editorType: 'SelectBox',
    isRequired: true,
    editorOptions: {
      placeholder: 'Choose an option',
      items: categories,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  type: {
    dataField: 'type',
    label: 'Work Order Type',
    editorType: 'SelectBox',
    isRequired: true,
    editorOptions: {
      placeholder: 'Choose an option',
      items: types,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  priority: {
    dataField: 'priority',
    label: 'Priority',
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Choose an option',
      items: priorities,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  impact: {
    dataField: 'impact',
    label: 'Impact',
    editorType: 'SelectBox',
    editorOptions: {
      placeholder: 'Choose an option',
      items: impacts,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  assignmentGroupId: {
    dataField: 'assignmentGroupId',
    label: 'Assigned Group',
  },
  notes: {
    dataField: 'notes',
    label: 'Notes',
    editorType: 'TextArea',
  },
  estimatedCostValue: {
    dataField: 'estimatedCostValue',
    label: 'Estimated Cost',
    editorType: 'Currency',
    editorOptions: {
      placeholder: 'Enter amount',
      min: 0,
      step: 0.01,
      max: 1000000,
      format: {
        type: 'currency',
        precision: 2,
      },
      showSpinButtons: true,
    },
  },
  estimatedEffort: {
    dataField: 'estimatedEffort',
    label: 'Estimated Effort (hours)',
    editorType: 'NumberBox',
    editorOptions: {
      format: '#,##0.##',
      step: 0.01,
      min: 0,
      max: 1000000,
      showSpinButtons: true,
    },
  },
} satisfies FieldDefs<WorkOrderTemplate>;

export default fields;
